import React, { useContext, createContext, useEffect, useState, PropsWithChildren, memo } from 'react';
import { View, Text, ImageBackground, ImageBackgroundProps, TextProps, ScrollViewProps, Animated, FlatListProps, ViewProps } from 'react-native';
import Image, { IKwaiImageProps } from '@kibt/meta-ui-krn/lib/components/Image';
import { rtlCovertFn, rtlDirectionFn } from '@/common/rn';
import { useLogAttribute } from '@/common/hooks';
import { isAndroidKDS, isWeb } from '@/common/device-info';
import { $isRtl } from '@/common/i18n';
import { isEqual } from 'lodash';
import { FlatList, ScrollView } from '../GestureHandler';
import { useIsFocused } from '@/hooks/useIsFocused';
import { KeyboardAwareScrollView, KeyboardAwareScrollViewProps } from 'react-native-keyboard-aware-scroll-view';
import { useSlideBack } from '@/hooks/useSlideBack';
import { Swiper } from '@kibt/meta-ui-krn';
import { SwiperProps } from '@kds/kid-ui-plus';
import withImageWrapper from '@/common/perf/withImageWrapper';

export const PageContext = createContext({ Styles: {} });

function rtlClassNameFn(name: string | string[], n: any) {
    if (Array.isArray(name)) {
        const result = [
            name.map((className) => {
                return n[className];
            }),
            name.map((className) => {
                return n[`${className}-rtl`];
            }),
        ];
        return result;
    } else {
        return [n[name], n[`${name}-rtl`]];
    }
}
/**
 *
 * @props :
 * className H5样式属性
 * style  Krn样式属性(编译后)
 * rtlClassName 包含Krn构建时产出rtl样式，依据rtl判断生效对应样式
 * rtl 直接设置direction属性
 */
const useRTLContainer = (props) => {
    const { className, style, rtlClassName, rtl, ...extra } = props;
    const { Styles } = useContext(PageContext);
    let rtlClassNameData = rtlClassName;
    if (rtlClassName && Styles) {
        rtlClassNameData = rtlClassNameFn(rtlClassName, Styles);
    }
    let langStyle = className;
    // 强制设置Rtl方向
    const rtlDir = rtl === 'rtl' ? true : $isRtl();
    //KRN环境
    if (!isWeb) {
        //处理style属性rtl
        if (style && Array.isArray(style)) {
            langStyle = style
                .map((item) => rtlDirectionFn(rtlCovertFn(item, rtlDir), rtlDir))
                .filter((item) => !!item && Object.keys(item).length > 0)
                .reduce((prev, cur) => {
                    return { ...prev, ...cur };
                }, {});
        } else if (style) {
            langStyle = rtlDirectionFn(rtlCovertFn(style, rtlDir), rtlDir);
        }
        //应用构建产出rtl
        const rtlStyle = rtlClassNameData && rtlClassNameData[rtlDir ? 1 : 0];
        const defaultStyle = rtlClassNameData && rtlClassNameData[0];
        if (defaultStyle) {
            if (Array.isArray(rtlStyle)) {
                const rtlStyleObj = rtlStyle.reduce((prev, curStyle, index) => {
                    return { ...prev, ...(curStyle || defaultStyle[index]) };
                }, {});
                langStyle = { ...rtlStyleObj, ...langStyle };
            } else {
                langStyle = { ...(rtlStyle || defaultStyle), ...langStyle };
            }
            if (rtlDir && langStyle.flexDirection === 'row-reverse') {
                langStyle = rtlCovertFn(langStyle, rtlDir);
            }
        }
        //h5环境应用默认class即可
    } else if (rtlClassNameData) {
        langStyle = rtlClassNameData && rtlClassNameData[0];
    }
    useLogAttribute(props.vLog);
    const classProps = {
        style: !isWeb ? langStyle : rtl ? { ...style, direction: rtl } : style,
        className: isWeb ? langStyle : {},
    };
    return { classProps, extra };
};

interface RtlProps {
    style?: React.CSSProperties;
    className?: string;
    rtlClassName?: string | string[];
    rtl?: string;
    vLog?: any;
}

interface imageUri {
    uri: string;
}

interface KImageProps extends RtlProps {
    source: imageUri;
}

const RtlImage: React.FC<KImageProps & IKwaiImageProps> = React.forwardRef((props, ref) => {
    const { classProps, extra } = useRTLContainer(props);
    return (
        <Image ref={ref} {...classProps} {...extra}>
            {props.children}
        </Image>
    );
});

export const KImage = memo(withImageWrapper(RtlImage), isEqual);

const RtlImageBackground: React.FC<RtlProps & ImageBackgroundProps> = (props) => {
    const { classProps, extra } = useRTLContainer(props);
    return (
        <ImageBackground {...classProps} {...extra}>
            {props.children}
        </ImageBackground>
    );
};

export const KImageBackground = withImageWrapper(RtlImageBackground);

export const KView: React.FC<RtlProps | ViewProps> = React.forwardRef((props, ref) => {
    const { classProps, extra } = useRTLContainer(props);
    return (
        <View ref={ref} {...classProps} {...extra}>
            {props.children}
        </View>
    );
});

export const KScrollView: React.FC<RtlProps & ScrollViewProps> = React.forwardRef((props, ref) => {
    const { classProps, extra } = useRTLContainer(props);
    // 处理容器右滑返回的问题
    const { setSlideBack } = useSlideBack();
    const { horizontal = false } = props;
    const slideBackProps = horizontal
        ? {
              onTouchStart: (evt) => {
                  setSlideBack(false);
                  (props as any).onTouchStart?.(evt);
              },
              onTouchCancel: (evt) => {
                  setSlideBack(true);
                  (props as any).onTouchCancel?.(evt);
              },
              onTouchEnd: (evt) => {
                  setSlideBack(true);
                  (props as any).onTouchEnd?.(evt);
              },
              onScrollBegin: (evt) => {
                  setSlideBack(false);
                  (props as any).onScrollBegin?.(evt);
              },
              onScrollEnd: (evt) => {
                  setSlideBack(true);
                  (props as any).onScrollEnd?.(evt);
              },
              onScrollBeginDrag: (evt) => {
                  setSlideBack(false);
                  (props as any).onScrollBeginDrag?.(evt);
              },
              onScrollEndDrag: (evt) => {
                  setSlideBack(true);
                  (props as any).onScrollEndDrag?.(evt);
              },
          }
        : {};

    return (
        <ScrollView ref={ref} {...classProps} {...extra} {...slideBackProps}>
            {props.children}
        </ScrollView>
    );
});
KScrollView.displayName = 'KScrollView';

export const KText: React.FC<RtlProps & TextProps> = React.forwardRef((props, ref) => {
    const { classProps, extra } = useRTLContainer(props);
    return (
        <Text ref={ref} {...classProps} {...extra}>
            {props.children}
        </Text>
    );
});

export const KSwiper = React.forwardRef((props: SwiperProps, ref) => {
    // 处理容器右滑返回的问题
    const { setSlideBack } = useSlideBack();
    const slideBackProps = {
        onTouchStart: (evt) => {
            setSlideBack(false);
            (props as any).onTouchStart?.(evt);
        },
        onTouchCancel: (evt) => {
            setSlideBack(true);
            (props as any).onTouchCancel?.(evt);
        },
        onTouchEnd: (evt) => {
            setSlideBack(true);
            (props as any).onTouchEnd?.(evt);
        },
    };
    return <Swiper ref={ref} {...props} {...slideBackProps} />;
});
KScrollView.displayName = 'KSwiper';

export const KFlatList = React.forwardRef((props: FlatListProps, ref) => {
    // 处理容器右滑返回的问题
    const { setSlideBack } = useSlideBack();
    const { horizontal = false } = props;
    const slideBackProps = horizontal
        ? {
              onTouchStart: (evt) => {
                  setSlideBack(false);
                  (props as any).onTouchStart?.(evt);
              },
              onTouchCancel: (evt) => {
                  setSlideBack(true);
                  (props as any).onTouchCancel?.(evt);
              },
              onTouchEnd: (evt) => {
                  setSlideBack(true);
                  (props as any).onTouchEnd?.(evt);
              },
              onScrollBegin: (evt) => {
                  setSlideBack(false);
                  (props as any).onScrollBegin?.(evt);
              },
              onScrollEnd: (evt) => {
                  setSlideBack(true);
                  (props as any).onScrollEnd?.(evt);
              },
              onScrollBeginDrag: (evt) => {
                  setSlideBack(false);
                  (props as any).onScrollBeginDrag?.(evt);
              },
              onScrollEndDrag: (evt) => {
                  setSlideBack(true);
                  (props as any).onScrollEndDrag?.(evt);
              },
          }
        : {};
    return <FlatList ref={ref} {...props} {...slideBackProps} />;
});
KFlatList.displayName = 'KFlatList';

/**
 * 技术方案 https://docs.corp.kuaishou.com/k/home/VRnhDphNUJtY/fcAAgc0pQfFd-Ct0QfCzHz6w7
 */
export function withWrapper<T extends object>(WrappedComponent) {
    return React.forwardRef((props: T & RtlProps & PropsWithChildren<any>, ref) => {
        const { classProps, extra } = useRTLContainer(props);
        const isFocused = useIsFocused();
        const [nativeID, setNativeID] = useState<'draggable_scroll_view_id' | ''>(isFocused ? 'draggable_scroll_view_id' : '');

        useEffect(() => {
            if (isAndroidKDS) {
                setNativeID(isFocused ? 'draggable_scroll_view_id' : '');
            }
        }, [isFocused]);

        useEffect(() => {
            if (nativeID && isFocused && isAndroidKDS) {
                // 客户端没有发版，后面上半屏的时候再发版
                // if (versionCompare(appVersion, '10.0.30') >= 0) {
                //     kwaiInvoke('component.clearDraggableScrollView', {});
                // }
            }
        }, [nativeID, isFocused]);
        return <WrappedComponent ref={ref} nativeID={nativeID} {...classProps} {...extra} {...props} />;
    });
}

export const KDraggableScrollView = withWrapper<ScrollViewProps>(KScrollView);
export const KDraggableFlatList = withWrapper(KFlatList) as typeof FlatList;
export const KDraggableAnimatedFlatList = withWrapper(Animated.FlatList) as typeof Animated.FlatList;
export const KDraggableKeyboardAwareScrollView = withWrapper<KeyboardAwareScrollViewProps>(KeyboardAwareScrollView);
